import usePlayer from '../contexts/usePlayer';
import PlayButton from './PlayButton';
import BlurFillImage from './BlurFillImage';

function Title({ title, sourceUrl }: { title: string; sourceUrl?: string }) {
  const domain = sourceUrl
    ? (() => {
        try {
          const url = new URL(sourceUrl);
          return url.hostname.replace('www.', '');
        } catch {
          return '';
        }
      })()
    : '';

  const text = `${title}${domain ? ` | ${domain}` : ''}`;

  return (
    <div
      style={{
        width: '100%',
        color: 'var(--accent)',
        fontSize: '0.9rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
    </div>
  );
}

function TimeDisplay({
  currentTime,
  totalTime,
}: {
  currentTime: number;
  totalTime: number;
}) {
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      style={{
        fontSize: '0.75rem',
        color: 'var(--primary-soft)',
      }}
    >
      {formatTime(currentTime)} / {formatTime(totalTime)}
    </div>
  );
}

export default function PlayBar({ onOpen }: { onOpen: () => void }) {
  const { nowPlaying, currentPlaylist, currentTime } = usePlayer();

  if (!nowPlaying) return null;

  const displayTotalTime = nowPlaying.secondsTotal || 0;
  const displayCurrentTime = currentTime;

  // For playlist, show playlist title | chapter title
  const displayTitle = currentPlaylist
    ? `${currentPlaylist.title} | ${nowPlaying.title}`
    : nowPlaying.title;

  // Use playlist image if available
  const displayImage = currentPlaylist?.imageUrl || nowPlaying.imageUrl;

  return (
    <div
      onClick={onOpen}
      style={{
        height: '56px',
        boxShadow: '0 -1px 0 var(--border), var(--shadow-md)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'var(--card-bg)',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--card-bg)';
      }}
    >
      <BlurFillImage
        imageUrl={displayImage}
        style={{
          width: '56px',
          height: '56px',
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          padding: '0 12px',
          gap: '2px',
        }}
      >
        <Title
          title={displayTitle}
          sourceUrl={
            nowPlaying.type === 'content' && !currentPlaylist
              ? nowPlaying.sourceUrl
              : undefined
          }
        />
        <TimeDisplay
          currentTime={displayCurrentTime}
          totalTime={displayTotalTime}
        />
      </div>
      <div style={{ marginRight: '8px' }}>
        <PlayButton size={40} />
      </div>
    </div>
  );
}
